import React, { useCallback, useEffect, useMemo, useState } from "react";
import { doc, getDoc, getFirestore, serverTimestamp, setDoc } from "firebase/firestore";
import { getDownloadURL, getStorage, ref as storageRef, uploadBytes } from "firebase/storage";

const VIDEO_NAME_REGEX = /^[a-zA-Z0-9_-]+$/;

const cardStyle = {
  background: "#fff",
  border: "1px solid #e5e7eb",
  borderRadius: 12,
  padding: 12,
  display: "flex",
  flexDirection: "column",
  gap: 10
};

const normalizeVideoVariables = async ({ raw, storage }) => {
  const source = raw && typeof raw === "object" && !Array.isArray(raw) ? raw : {};
  const entries = await Promise.all(Object.entries(source).map(async ([name, meta]) => {
    const storagePath = String(meta?.storagePath || "");
    const url = storagePath
      ? await getDownloadURL(storageRef(storage, storagePath)).catch(() => meta?.url || "")
      : (meta?.url || "");
    return [name, { ...(meta || {}), name, storagePath, url }];
  }));
  return Object.fromEntries(entries);
};

const VideoVariablesSection = ({ isActive, podcastId, onVideoVariablesChange }) => {
  const db = useMemo(() => getFirestore(), []);
  const storage = useMemo(() => getStorage(), []);
  const podcastDocRef = useMemo(() => {
    if (!podcastId) return null;
    return doc(db, "podcasts", String(podcastId));
  }, [db, podcastId]);

  const [videoName, setVideoName] = useState("");
  const [videoVariables, setVideoVariables] = useState({});
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");

  const loadVideoVariables = useCallback(async () => {
    if (!podcastDocRef) return;
    try {
      setLoading(true);
      setError("");
      const snap = await getDoc(podcastDocRef);
      const normalized = await normalizeVideoVariables({
        raw: snap.exists() ? snap.data()?.video_variables : {},
        storage
      });
      setVideoVariables(normalized);
      onVideoVariablesChange?.(normalized);
    } catch (err) {
      console.error("Failed to load video variables:", err);
      setError("Failed to load video variables.");
    } finally {
      setLoading(false);
    }
  }, [podcastDocRef, storage, onVideoVariablesChange]);

  useEffect(() => {
    if (!isActive || !podcastDocRef) return;
    loadVideoVariables();
  }, [isActive, podcastDocRef, loadVideoVariables]);

  const uploadVideoVariable = async (file) => {
    const key = String(videoName || "").trim();
    if (!podcastDocRef || !podcastId) return;
    if (!key || !VIDEO_NAME_REGEX.test(key)) {
      setError("Use letters, numbers, hyphen, or underscore for the video name.");
      setStatus("");
      return;
    }
    if (!file) {
      setError("Choose an MP4 file first.");
      setStatus("");
      return;
    }
    if (file.type && file.type !== "video/mp4") {
      setError("Hero videos must be MP4 files.");
      setStatus("");
      return;
    }

    try {
      setUploading(true);
      setStatus("");
      setError("");

      const storagePath = `Podcasts/${String(podcastId)}/hero-videos/${key}.mp4`;
      await uploadBytes(storageRef(storage, storagePath), file, { contentType: file.type || "video/mp4" });
      const url = await getDownloadURL(storageRef(storage, storagePath)).catch(() => "");

      const snap = await getDoc(podcastDocRef);
      const existing = snap.exists() && snap.data()?.video_variables && typeof snap.data().video_variables === "object"
        ? snap.data().video_variables
        : {};
      const next = {
        ...existing,
        [key]: {
          name: key,
          storagePath,
          url,
          fileName: file.name || `${key}.mp4`,
          contentType: file.type || "video/mp4",
          uploadedAt: new Date().toISOString()
        }
      };

      await setDoc(podcastDocRef, { video_variables: next, updatedAt: serverTimestamp() }, { merge: true });
      const normalized = await normalizeVideoVariables({ raw: next, storage });
      setVideoVariables(normalized);
      onVideoVariablesChange?.(normalized);
      setStatus(`Uploaded ${key}.`);
    } catch (err) {
      console.error("Failed to upload video variable:", err);
      setError("Failed to upload video variable.");
    } finally {
      setUploading(false);
    }
  };

  const removeVideoVariable = async (key) => {
    if (!podcastDocRef || !key) return;
    try {
      setStatus("");
      setError("");
      const next = { ...videoVariables };
      delete next[key];
      await setDoc(podcastDocRef, { video_variables: next, updatedAt: serverTimestamp() }, { merge: true });
      setVideoVariables(next);
      onVideoVariablesChange?.(next);
      setStatus(`Removed ${key}.`);
    } catch (err) {
      console.error("Failed to remove video variable:", err);
      setError("Failed to remove video variable.");
    }
  };

  return (
    <div style={cardStyle}>
      <div style={{ fontWeight: 800, fontSize: 16 }}>Video variables</div>
      <p style={{ margin: 0, color: "#4b5563", lineHeight: 1.4 }}>
        Upload muted visual-only MP4 clips that route scenes can reference by video name.
      </p>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
        <input
          type="text"
          value={videoName}
          onChange={(e) => setVideoName(e.target.value)}
          placeholder="video_name"
          style={{
            minWidth: 180,
            padding: "9px 10px",
            borderRadius: 8,
            border: "1px solid #d1d5db",
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace"
          }}
        />
        <label
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 10,
            padding: "8px 12px",
            borderRadius: 8,
            border: "1px solid #2563eb",
            background: "#dbeafe",
            color: "#1d4ed8",
            fontWeight: 700,
            cursor: uploading ? "default" : "pointer",
            opacity: uploading ? 0.65 : 1
          }}
        >
          {uploading ? "Uploading..." : "Upload MP4"}
          <input
            type="file"
            accept="video/mp4,.mp4"
            disabled={uploading}
            onChange={(e) => {
              const file = e.target.files && e.target.files[0];
              if (file) uploadVideoVariable(file);
              e.target.value = "";
            }}
            style={{ display: "none" }}
          />
        </label>
        <button
          onClick={loadVideoVariables}
          disabled={loading || !podcastDocRef}
          style={{
            padding: "8px 12px",
            borderRadius: 8,
            border: "1px solid #e5e7eb",
            background: "#f3f4f6",
            color: "#374151",
            fontWeight: 600,
            cursor: "pointer"
          }}
        >
          {loading ? "Loading..." : "Reload"}
        </button>
      </div>

      {Object.keys(videoVariables).length > 0 ? (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
            gap: 12
          }}
        >
          {Object.entries(videoVariables).map(([key, video]) => (
            <div
              key={key}
              style={{
                border: "1px solid #e5e7eb",
                borderRadius: 10,
                padding: 10,
                background: "#f9fafb",
                display: "grid",
                gap: 8
              }}
            >
              {video?.url ? (
                <video
                  src={video.url}
                  muted
                  controls
                  playsInline
                  preload="metadata"
                  style={{
                    width: "100%",
                    height: 100,
                    objectFit: "cover",
                    background: "#000",
                    borderRadius: 8
                  }}
                />
              ) : (
                <div style={{ height: 100, display: "grid", placeItems: "center", color: "#6b7280" }}>No preview</div>
              )}
              <div
                title={key}
                style={{
                  fontSize: 12,
                  color: "#374151",
                  fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap"
                }}
              >
                {key}
              </div>
              <button
                onClick={() => removeVideoVariable(key)}
                style={{
                  border: "1px solid #fecaca",
                  background: "#fee2e2",
                  color: "#991b1b",
                  borderRadius: 8,
                  padding: "6px 8px",
                  fontWeight: 700,
                  cursor: "pointer"
                }}
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      ) : (
        <div style={{ color: "#6b7280" }}>No custom video variables uploaded yet.</div>
      )}

      {(status || error) && (
        <div style={{ color: error ? "#b91c1c" : "#065f46", fontWeight: 700 }}>
          {error || status}
        </div>
      )}
    </div>
  );
};

export default VideoVariablesSection;
